import * as React from 'react';

import Grid from '@mui/material/Grid';

import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';

import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LayersIcon from '@mui/icons-material/Layers';
import LayersClearIcon from '@mui/icons-material/LayersClear';
import LinkIcon from '@mui/icons-material/Link';

import Stack from '@mui/material/Stack';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import Group from './Group';
import Filter from './Filter';

import { useTheme } from '@mui/material/styles';

import {ATTRIBUTES, CLOSE, CONFIG_DIALOG_INSTRUCTIONS, CONFIG_DIALOG_TITLE, CREDITPOINTS, STUDY_PROGRAM, ALL_STUDY_PROGRAMS, INSTRUCTORS, LANGUAGES, LEVELS, SEARCH, SEMESTERS, filterOptions, LINK_COPIED} from './Data'

export default function GroupingFilterDialog({groupingState, instructionFilterState, levelFilterState, cpFilterState,
		attributeFilterState, languageFilterState, semesterFilterState, filterDialogState, searchState, showDetailsState,
		studyProgramState, getConfigUrl }) {

	// grouping (array of Strings)
	const [grouping, setGrouping] = groupingState;

	// filters
	const [instructorFilter, setInstructorFilter] = instructionFilterState;
	const [levelFilter, setLevelFilter] = levelFilterState;
	const [cpFilter, setCpFilter] = cpFilterState;
	const [attributeFilter, setAttributeFilter] = attributeFilterState;
	const [languageFilter, setLanguageFilter] = languageFilterState;
	const [semesterFilter, setSemesterFilter] = semesterFilterState;
	const [showDetails, setShowDetails] = showDetailsState;
	const [studyProgram, setStudyProgram] = studyProgramState;

	const [search, setSearch] = searchState;

	// state of showing/hiding the filter dialog
	const [filterDialogOpen, setFilterDialogOpen] = filterDialogState;
	const [snackbarOpen, setSnackbarOpen] = React.useState(false);
	
	const theme = useTheme();

	// define GUI elements
	return (
		<React.Fragment>
			<Grid container style={{ gap: 15 }}>
				<Grid xs='auto' >
					<ButtonGroup variant="contained" aria-label="outlined primary button group">
						<Button
							onClick={() => {
								setGrouping([]);
								setInstructorFilter([]);
								setLevelFilter([]);
								setCpFilter([]);
								setAttributeFilter([]);
								setLanguageFilter([]);
								setSemesterFilter([]);
								setStudyProgram("");
							}}
							disabled={
								grouping.length === 0 &&
								instructorFilter.length === 0 &&
								levelFilter.length === 0 &&
								cpFilter.length === 0 &&
								attributeFilter.length === 0 &&
								languageFilter.length === 0 &&
								semesterFilter.length === 0 &&
								studyProgram === ""}
						>
							<FilterAltOffIcon />
						</Button>
						<Button
							onClick={() => { setFilterDialogOpen(true) }}
						>
							<FilterAltIcon />{/*FILTER_BUTTON_LABEL[theme.language]*/}
						</Button>
						<Button variant={showDetails ? "contained" : "outlined"} onClick={() => setShowDetails(!showDetails)}>{showDetails ? <LayersIcon /> : <LayersClearIcon />}</Button>
    					<Button variant="contained" onClick={() => {navigator.clipboard.writeText(getConfigUrl()); setSnackbarOpen(true);}}>
							<LinkIcon />
							<Snackbar
								open={snackbarOpen}
								autoHideDuration={6000}
								onClose={() => {
									setSnackbarOpen(false);}}
							>
					        	<Alert
					          	severity="success"
					          	style={{textTransform: 'none', color: 'white'}}
					        	>
					        	{LINK_COPIED[theme.language]}
					        	</Alert>
					      	</Snackbar>
						</Button>
					</ButtonGroup>
				</Grid>
				<Grid xs >
					<TextField id="outlined-basic" variant="outlined" size="small" label={SEARCH[theme.language]}
						value={search}
						onChange={(event) => {
							setSearch(event.target.value);
						}}
						fullWidth
						InputProps={{
							startAdornment:
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>,
							endAdornment:
								<InputAdornment position="end">
									{search ?
										<IconButton
											aria-label="clear-search"
											onClick={() => setSearch('')}
											onMouseDown={(event) => event.preventDefault()}
											edge="end"
										>
											<ClearIcon />
										</IconButton>
										: <> </>}
								</InputAdornment>
						}} />
				</Grid>
			</Grid>
			<Dialog
				open={filterDialogOpen}
				PaperProps={{
					component: 'form',
					onSubmit: (event) => {
						event.preventDefault();
						setFilterDialogOpen(false);
					},
				}}
				fullWidth
				maxWidth="sm"
  			>
				<DialogTitle>{CONFIG_DIALOG_TITLE[theme.language]}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{CONFIG_DIALOG_INSTRUCTIONS[theme.language]}
					</DialogContentText>
					<Stack spacing={2}>
						<Stack spacing={2} direction="row">
							<Group selectionState={[grouping, setGrouping]} />
						</Stack>
						<Stack spacing={2} direction="row">
							<Filter selectionState={[instructorFilter, setInstructorFilter]} category={INSTRUCTORS[theme.language]} options={filterOptions[theme.language].instructors} />
							<Filter selectionState={[levelFilter, setLevelFilter]} category={LEVELS[theme.language]} options={filterOptions[theme.language].levels} />
							<Filter selectionState={[cpFilter, setCpFilter]} category={CREDITPOINTS[theme.language]} options={filterOptions[theme.language].creditPoints} />
						</Stack>
						<Stack spacing={2} direction="row">
							<Filter selectionState={[attributeFilter, setAttributeFilter]} category={ATTRIBUTES[theme.language]} options={filterOptions[theme.language].attributes} />
							<Filter selectionState={[languageFilter, setLanguageFilter]} category={LANGUAGES[theme.language]} options={filterOptions[theme.language].languages} />
							<Filter selectionState={[semesterFilter, setSemesterFilter]} category={SEMESTERS[theme.language]} options={filterOptions[theme.language].semesters} />
						</Stack>
						<Stack spacing={2} direction="row">
							<Filter selectionState={[studyProgram, setStudyProgram]} category={STUDY_PROGRAM[theme.language]} options={filterOptions[theme.language].studyPrograms} singleChoice={true} defaultOption={ALL_STUDY_PROGRAMS[theme.language]}/>
						</Stack>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button type="submit">{CLOSE[theme.language]}</Button>
				</DialogActions>
			</Dialog>
		</React.Fragment>
	);
}